// How finance names a period, a job, an invoice and a count, in one place (finance-real, the user 2026-10-23: "same
// entities should use same components ... so that the user doesn't see the same entity in different but similar ways").
// The server stand-in names its periods and team invoices with these too, so a page and a refusal read the same.
import { ENTITY_CONFIG } from "@/core/entityConfig";
import type { JobInvoiceStatus, JobRhythm, TeamInvoiceStatus } from "../types";
import { dateLabel, daysLabel, monthLabel } from "./format";

const iso = (y: number, m: number, d: number): string => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** The last day of `day`'s calendar month. */
const lastOfMonth = (day: string): string => {
  const [y, m] = day.split("-").map(Number);
  return iso(y, m, new Date(Date.UTC(y, m, 0)).getUTCDate());
};

/**
 * Every period shown, one way: a calendar month "October 2026", any other run of days "21 Oct-20 Nov 2026" ("11-14 Nov
 * 2026" in one month), a day "8 Sep 2026". `to` empty or null is one day.
 */
export function periodText(from: string, to: string | null): string {
  if (!to || to === from) return dateLabel(from);
  if (from.endsWith("-01") && to === lastOfMonth(from)) return monthLabel(from.slice(0, 7));
  return daysLabel(from, to);
}

/** A job with no days set yet, in every list and the server stand-in's period label. */
export const NO_PERIOD = "No period yet";

/** A job named in one string: "Social Media Management · October 2026", so two periods of a series never read the same. */
export const jobLabel = (name: string, periodLabel: string): string => `${name} · ${periodLabel}`;

const JOB = ENTITY_CONFIG.contract;
const postsWord = ENTITY_CONFIG.post.plural.toLowerCase();

/** How a job bills (DD-061): "Recurring", "Every 8 posts", "One-off". */
export function rhythmText(job: { rhythm: JobRhythm }): string {
  switch (job.rhythm.mode) {
    case "recurring":
      return "Recurring";
    case "posts":
      return `Every ${job.rhythm.every} ${postsWord}`;
    case "one_off":
      return "One-off";
  }
}

/**
 * A job's line under its name, in one order on every job row: [client · ] period · how it bills ("Mister Coffee · October
 * 2026 · Recurring"). The client only where the list holds several clients' jobs (/finance's Jobs, a team invoice and its
 * pickers, To do), never on a client's own page or in a client invoice's drawer.
 */
export const jobSubline = (job: { periodLabel: string; rhythm: JobRhythm }, client?: string): string =>
  [client, job.periodLabel, rhythmText(job)].filter((p): p is string => !!p).join(" · ");

/** The agency's own client in /finance's lists (the user 2026-10-24: "an agency job I add that has no client"): Agency,
 * never its client's name, which its own page and Setup keep (TD-105). */
export const AGENCY = "Agency";

/** The own client as the job form's client picker offers it, first. */
export const AGENCY_CHOICE = `${AGENCY} (no ${ENTITY_CONFIG.client.label.toLowerCase()})`;

/** A client as /finance's lists name it: Agency for the own client. */
export const clientName = (name: string, own: boolean): string => (own ? AGENCY : name);

const CLAIM = ENTITY_CONFIG.claim;
const INVOICE = ENTITY_CONFIG.invoice;
const TEAM = ENTITY_CONFIG.teamInvoice;

const counted = (n: number, one: string, many: string): string => `${n} ${(n === 1 ? one : many).toLowerCase()}`;

/** "1 job", "3 jobs": what a client invoice counts. */
export const jobsCount = (n: number): string => counted(n, JOB.label, JOB.plural);

/** "1 claim", "3 claims": what a team invoice counts. */
export const claimsCount = (n: number): string => counted(n, CLAIM.label, CLAIM.plural);

/** The one empty claim list. */
export const NO_CLAIM = `No ${CLAIM.label.toLowerCase()} yet.`;

/** What an invoice name reads from: a client invoice's number and day, a team invoice's received day. */
export type NamedInvoice =
  | { kind: "client"; status: JobInvoiceStatus; number: string | null; sentOn: string | null }
  | { kind: "team"; status: TeamInvoiceStatus; receivedOn: string | null };

/**
 * An invoice's one name, in tables, drawer titles, list rows and references: "Draft invoice", "Invoice INV2600048"
 * ("Invoice · 18 Sep 2026" with no number), "Draft team invoice", "Team invoice · 12 Oct 2026" (the day it arrived). A
 * drawer on /finance puts whose it is in front: "Mister Coffee · Invoice INV2600048", "Ana · Team invoice · 12 Oct 2026".
 */
export function invoiceName(i: NamedInvoice): string {
  if (i.kind === "client") {
    if (i.status === "draft") return `Draft ${INVOICE.label.toLowerCase()}`;
    return i.number ? `${INVOICE.label} ${i.number}` : `${INVOICE.label} · ${dateLabel(i.sentOn)}`;
  }
  if (i.status === "draft") return `Draft ${TEAM.label.toLowerCase()}`;
  return `${TEAM.label} · ${dateLabel(i.receivedOn)}`;
}

/** What a status-date line reads from. */
export type DatedInvoice =
  | { kind: "client"; status: JobInvoiceStatus; createdOn: string; sentOn: string | null; paidOn: string | null }
  | { kind: "team"; status: TeamInvoiceStatus; createdOn: string; receivedOn: string | null; paidOn: string | null };

const STATUS_WORD: Record<JobInvoiceStatus | TeamInvoiceStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  overdue: "Overdue",
  partial: "Partly paid",
  paid: "Paid",
  received: "Received",
};

/**
 * An invoice's one status-date line, wherever a client or team invoice is listed or referenced, always led by its status:
 * "<Status> <its day>" ("Sent 18 Sep 2026", "Received 12 Oct 2026", "Paid 15 Oct 2026"), a draft "Draft · started 3 Oct
 * 2026", and a status with no day of its own the day it went out ("Overdue · sent 18 Sep 2026", "Partly paid · sent 18
 * Sep 2026"). A list adds its count after it (`withCount`), and To do's Waiting its age (`ageText`).
 */
export function invoiceDateLine(i: DatedInvoice): string {
  const word = STATUS_WORD[i.status];
  if (i.status === "draft") return `${word} · started ${dateLabel(i.createdOn)}`;
  if (i.kind === "team") return `${word} ${dateLabel(i.status === "paid" ? (i.paidOn ?? i.receivedOn) : i.receivedOn)}`;
  if (i.status === "sent") return `${word} ${dateLabel(i.sentOn)}`;
  if (i.status === "paid" && i.paidOn) return `${word} ${dateLabel(i.paidOn)}`;
  return `${word} · sent ${dateLabel(i.sentOn)}`;
}

/** The date line with what the invoice counts after it: "Sent 18 Sep 2026 · 3 jobs", "Draft · started 21 Oct 2026 · 4 claims". */
export const withCount = (dateLine: string, count: string): string => `${dateLine} · ${count}`;

/** An invoice's age, the one segment wherever it is shown: "1 day", "33 days". */
export const ageText = (days: number): string => `${days} ${days === 1 ? "day" : "days"}`;

/** It still waits for money from the client: sent, overdue or partly paid. */
export const awaitsPayment = (status: JobInvoiceStatus): boolean => status === "sent" || status === "overdue" || status === "partial";
